/**
 * Dev Tool Service
 * Developer tools and debugging endpoints
 */

import api from '../utils/api';

export const devService = {
  /**
   * Get Inshorts API logs
   * Only accessible by admin and super_admin
   * Pass { hashId, newsId } to search logs for a specific news - newsId also
   * catches calls logged before Inshorts returned a hash (e.g. create_post)
   */
  getApiLogs: async (params = {}) => {
    const response = await api.get('/dev/logs', { params });
    return response.data;
  },

  /**
   * Get not supported domains
   * Only accessible by admin and super_admin
   */
  getNotSupportedDomains: async () => {
    const response = await api.get('/dev/not-supported-domains');
    return response.data;
  },

  /**
   * Get both halves of the fetch-failure picture, each row carrying the errors
   * recorded against it:
   * - `erroredNews`: every news record with a fetch error on it, whatever its
   *   status - the ones that never became news are hidden from the listings,
   *   so this is the only view of them
   * - `rescuedNews`: news the server could not fetch but the user's browser
   *   could - real news, but the server's failure is only visible here
   * Only accessible by admin and super_admin
   */
  getErroredNews: async () => {
    const response = await api.get('/dev/errored-news');
    return response.data;
  },

  /**
   * Delete one errored news record from the database.
   * The server only removes parked fetch failures, never real news that merely
   * recorded an error - hence the `deletable` flag on each row.
   * Only accessible by admin and super_admin
   */
  deleteErroredNews: async (newsId) => {
    const response = await api.delete(`/dev/errored-news/${newsId}`);
    return response.data;
  },

  /**
   * Delete every parked fetch failure, keeping the real news in the list
   * Only accessible by admin and super_admin
   */
  clearErroredNews: async () => {
    const response = await api.delete('/dev/errored-news');
    return response.data;
  },

  /**
   * Remove one URL from a domain's failure history
   * Lives under /not-supported-domains rather than /dev - the domain's own
   * router owns writes, /dev only reads the list
   * Only accessible by admin and super_admin
   */
  removeNotSupportedDomainUrl: async (domain, urlId) => {
    const response = await api.delete(
      `/not-supported-domains/${encodeURIComponent(domain)}/urls/${urlId}`
    );
    return response.data;
  },

  /**
   * Remove a domain from the not supported list entirely
   * Only accessible by admin and super_admin
   */
  removeNotSupportedDomain: async (domain) => {
    const response = await api.delete(`/not-supported-domains/${encodeURIComponent(domain)}`);
    return response.data;
  },

  /**
   * Get enabled states for regional feed
   * Accessible by authenticated users
   */
  getStates: async () => {
    const response = await api.get('/dev/states');
    return response;
  },

  /**
   * Get districts by state code with keyword search
   * Accessible by authenticated users
   */
  getDistricts: async (stateCode, search = '') => {
    const response = await api.get('/dev/districts', {
      params: { stateCode, search }
    });
    return response;
  },
};
